import type { FormularioDaObra } from './formularioDaObra.js';

/**
 * O rascunho da obra em cadastro, guardado no navegador.
 *
 * O cadastro é uma página, e página se deixa sem querer: um clique no menu, o
 * "voltar" do navegador, a aba fechada. O que foi preenchido fica no
 * localStorage, por órgão e pessoa, e volta quando "Nova obra" é aberta de novo
 * — inclusive depois de recarregar. A capa volta numa miniatura reduzida: a
 * imagem já subiu, e o que o rascunho precisa é só de algo para mostrar.
 */
export interface CapaGuardada {
  readonly mediaId: string;
  /** Miniatura em `data:` — o endereço `blob:` do envio morre com a página. */
  readonly url: string;
}

export interface RascunhoDaObra {
  readonly versao: 1;
  readonly salvoEm: string;
  readonly form: FormularioDaObra;
  readonly capa: CapaGuardada | null;
  /** Quem entra na equipe como auxiliar, além do responsável. */
  readonly equipe: readonly string[];
  readonly indice: number;
  readonly alcancada: number;
}

/** Rascunho mais velho que isso não volta: a licitação já andou, e o cadastro também. */
const VALIDADE_MS = 30 * 24 * 60 * 60 * 1000;

export const chaveDoRascunhoDaObra = (organizacao: string, pessoa: string): string =>
  `obras.nova-obra:${organizacao}:${pessoa}`;

const CAMPOS_DE_TEXTO: readonly (keyof FormularioDaObra)[] = [
  'name',
  'description',
  'contractNumber',
  'initialValue',
  'status',
  'secretariaId',
  'companyId',
  'responsibleFiscalId',
  'contractStart',
  'contractEnd',
  'osStart',
  'osEnd',
  'visitIntervalDays',
  'coverMediaId',
  'latitude',
  'longitude',
  'address',
  'city',
  'state',
];

/**
 * Lê o que estava guardado, ou `null`. O registro pode ser de outra versão do
 * sistema, estar vencido ou nem ser um objeto: nada disso pode quebrar a tela,
 * e o que não serve mais sai do navegador. Campo que falta vem do `base` — o
 * formulário vazio de quem está cadastrando.
 */
export function lerRascunhoDaObra(
  chave: string,
  base: FormularioDaObra,
  ultimaEtapa: number,
): RascunhoDaObra | null {
  let guardado: string | null;
  try {
    guardado = window.localStorage.getItem(chave);
  } catch {
    return null;
  }
  if (guardado === null) return null;

  const rascunho = interpretar(guardado, base, ultimaEtapa);
  if (!rascunho) apagarRascunhoDaObra(chave);
  return rascunho;
}

function interpretar(
  guardado: string,
  base: FormularioDaObra,
  ultimaEtapa: number,
): RascunhoDaObra | null {
  let bruto: unknown;
  try {
    bruto = JSON.parse(guardado);
  } catch {
    return null;
  }
  if (!bruto || typeof bruto !== 'object') return null;
  const r = bruto as Record<string, unknown>;
  if (r['versao'] !== 1) return null;

  const salvoEm = typeof r['salvoEm'] === 'string' ? r['salvoEm'] : '';
  const quando = Date.parse(salvoEm);
  if (!Number.isFinite(quando) || Date.now() - quando > VALIDADE_MS) return null;

  const f = r['form'];
  if (!f || typeof f !== 'object') return null;
  const campos = f as Record<string, unknown>;

  const form: FormularioDaObra = { ...base };
  for (const campo of CAMPOS_DE_TEXTO) {
    const valor = campos[campo];
    if (typeof valor === 'string') (form[campo] as string) = valor;
  }
  if (typeof campos['isReform'] === 'boolean') form.isReform = campos['isReform'];

  const c = r['capa'];
  const capa =
    c &&
    typeof c === 'object' &&
    typeof (c as Record<string, unknown>)['mediaId'] === 'string' &&
    typeof (c as Record<string, unknown>)['url'] === 'string' &&
    String((c as Record<string, unknown>)['url']).startsWith('data:image/')
      ? {
          mediaId: String((c as Record<string, unknown>)['mediaId']),
          url: String((c as Record<string, unknown>)['url']),
        }
      : null;

  const etapa = (valor: unknown): number =>
    typeof valor === 'number' && Number.isInteger(valor)
      ? Math.min(Math.max(valor, 0), ultimaEtapa)
      : 0;

  return {
    versao: 1,
    salvoEm,
    // A capa sem miniatura não volta: o id sozinho prometeria uma imagem que a tela não mostra.
    form: capa ? form : { ...form, coverMediaId: '' },
    capa,
    equipe: Array.isArray(r['equipe'])
      ? r['equipe'].filter((id): id is string => typeof id === 'string')
      : [],
    indice: etapa(r['indice']),
    alcancada: etapa(r['alcancada']),
  };
}

/** Navegador sem armazenamento (modo privado, cota cheia) não é erro: o rascunho vale enquanto a página está aberta. */
export function gravarRascunhoDaObra(chave: string, rascunho: RascunhoDaObra): void {
  try {
    window.localStorage.setItem(chave, JSON.stringify(rascunho));
  } catch {
    // Sem armazenamento, o rascunho vive só em memória.
  }
}

export function apagarRascunhoDaObra(chave: string): void {
  try {
    window.localStorage.removeItem(chave);
  } catch {
    // Nada a apagar onde não há armazenamento.
  }
}

/**
 * Reduz a imagem enviada a uma miniatura em `data:`, para o rascunho mostrar a
 * capa depois de recarregar. 640px de largura e JPEG a 72%: algumas dezenas de
 * kilobytes, que cabem no localStorage com folga. Falhou, devolve `null` — o
 * rascunho segue sem a capa, e a tela diz isso ao voltar.
 */
export function miniaturaDaCapa(url: string): Promise<string | null> {
  return new Promise((resolver) => {
    const imagem = new Image();
    imagem.onload = () => {
      try {
        const escala = Math.min(1, 640 / imagem.naturalWidth);
        const largura = Math.max(1, Math.round(imagem.naturalWidth * escala));
        const altura = Math.max(1, Math.round(imagem.naturalHeight * escala));
        const tela = document.createElement('canvas');
        tela.width = largura;
        tela.height = altura;
        const contexto = tela.getContext('2d');
        if (!contexto) {
          resolver(null);
          return;
        }
        contexto.drawImage(imagem, 0, 0, largura, altura);
        resolver(tela.toDataURL('image/jpeg', 0.72));
      } catch {
        resolver(null);
      }
    };
    imagem.onerror = () => resolver(null);
    imagem.src = url;
  });
}
